import React, { JSX, useState } from "react";
import { useTranslation } from "react-i18next";

import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";

import Close from "@mui/icons-material/Close";

import { asTitle, deleteElement, isEmailValid } from "utils/Utils";

interface ShareExtractDialogProps {
	onClose: () => void;
	onConfirm: (users: string[]) => void;
}

export default function ShareExtractDialog(
	props: Readonly<ShareExtractDialogProps>
): JSX.Element {
	const [t] = useTranslation();

	const [addedUsers, setAddedUsers] = useState<string[]>([]);
	const [inputError, setInputError] = useState(false);

	function addUser(user: string): void {
		const email = user.trim().toLowerCase();
		if (!isEmailValid(email) || addedUsers.includes(email)) {
			setInputError(true);
			return;
		}

		setInputError(false);
		setAddedUsers(addedUsers.concat(email));
	}

	function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
		if (e.key === "Enter") {
			const element = e.target as HTMLInputElement;
			addUser(element.value);
		}
	}

	function handleBlur(e: React.FocusEvent<HTMLInputElement>) {
		addUser(e.target.value);
	}

	return (
		<Dialog open fullWidth maxWidth="xs" onClose={props.onClose}>
			<DialogTitle id="dialog-title">
				{asTitle(t("shareExtract"))}
			</DialogTitle>
			<DialogContent>
				<Grid container direction="column" spacing={2}>
					{/* // TODO: delete button? Or just TextArea? */}
					{addedUsers.map((user, index) => (
						<Grid item key={user}>
							<Grid container alignItems="center" spacing={2}>
								<Grid item>
									<IconButton
										size="small"
										onClick={() =>
											setAddedUsers(
												deleteElement(index, addedUsers)
											)
										}>
										<Close />
									</IconButton>
								</Grid>
								<Grid item>{user}</Grid>
							</Grid>
						</Grid>
					))}
					<Grid item>
						<TextField
							fullWidth
							key={addedUsers.length}
							error={inputError}
							onKeyDown={handleKeyDown}
							onBlur={handleBlur}
							variant="standard"
						/>
					</Grid>
				</Grid>
			</DialogContent>
			<DialogActions>
				<Button variant="outlined" onClick={props.onClose} autoFocus>
					{t("cancel")}
				</Button>
				<Button
					variant="contained"
					onClick={() => props.onConfirm(addedUsers)}>
					{t("confirm")}
				</Button>
			</DialogActions>
		</Dialog>
	);
}
